import { Controller } from "@hotwired/stimulus"

// MS50 #3003 — CSP-safe custom-field form behavior.
//
// Replaces the nonce-less inline <script> (blocked by the enforcing nonce CSP,
// #1222) that toggled the type-specific sections and populated the conditional-
// display value dropdown. Option add/remove is handled by the shared
// `nested-fields` controller; this controller owns the custom-field-specific
// concerns: show/hide the options/number/text sections by field type, and drive
// the "show when field X has value Y" conditional dropdown.
export default class extends Controller {
  static targets = [
    "optionsSection",
    "numberSection",
    "textSection",
    "conditionValueSection",
    "conditionValueSelect"
  ]
  static values = { fieldOptions: { type: Object, default: {} } }

  // Field-type <select> changed → reveal only the relevant constraint section.
  fieldTypeChanged(event) {
    const type = event.target.value

    if (this.hasOptionsSectionTarget) {
      this.#toggle(this.optionsSectionTarget, [ "select", "multi_select" ].includes(type))
    }
    if (this.hasNumberSectionTarget) {
      this.#toggle(this.numberSectionTarget, type === "number")
    }
    if (this.hasTextSectionTarget) {
      this.#toggle(this.textSectionTarget, [ "text", "textarea" ].includes(type))
    }
  }

  // Conditional-display "base field" <select> changed → show/populate the value
  // dropdown from the server-provided field-options map.
  conditionFieldChanged(event) {
    const fieldId = event.target.value

    if (!this.hasConditionValueSectionTarget) return

    if (!fieldId) {
      this.#toggle(this.conditionValueSectionTarget, false)
      if (this.hasConditionValueSelectTarget) {
        this.conditionValueSelectTarget.replaceChildren(new Option("Select a value", ""))
      }
      return
    }

    this.#toggle(this.conditionValueSectionTarget, true)

    const fieldData = this.fieldOptionsValue[fieldId]
    if (!this.hasConditionValueSelectTarget || !fieldData) return

    const select = this.conditionValueSelectTarget
    const previousValue = select.value
    const options = fieldData.type === "checkbox"
      ? [ new Option("Checked (Yes)", "1"), new Option("Unchecked (No)", "0") ]
      : (fieldData.options || []).map((option) => new Option(String(option), String(option)))

    select.replaceChildren(new Option("Select a value", ""), ...options)
    select.value = options.some((option) => option.value === previousValue) ? previousValue : ""
  }

  #toggle(element, visible) {
    // The type-specific sections render with .d-none by default; flip it.
    element.classList.toggle("d-none", !visible)
  }
}
